/**
 * TanStack Query hooks for the upload subsystem — the server-state layer the
 * upload flow and history UI sit on. Every hook calls a REAL contract from
 * `common/contracts/uploads.ts` through `apiClient` (ADHERENCE: no raw `fetch`
 * for internal endpoints), with hierarchical query keys and invalidate-after-
 * mutate.
 *
 * Hook map (one per contract the UX needs):
 *   - `usePreflight`     — mutation: headers → Loan/Tax/PII suggestions
 *   - `useAnalyze`       — mutation: a scrubbed upload → analysis (inference +
 *                          mapping suggestions + previous-match + readiness)
 *   - `useUploadsList`   — infinite/cursor query over upload history
 *   - `useUploadDetail`  — query for one historical file (analysis + mapping)
 *   - `useUpdateMapping` — mutation: persist a file's mapping (PUT)
 *   - `useDeleteUpload`  — mutation: soft-delete an upload event
 *   - `useDeleteUploadFile` — mutation: soft-delete one file in an event
 *   - `useBatchReadiness`— mutation: read-only batch validation preview (legacy
 *                          aggregate readiness + row-level loan-data validation)
 *   - `useFinalize`      — mutation: batch-accept after validation passes
 *
 * Mutation-result caveat (frontend-state skill): the flow advances steps from an
 * orchestration loop, so callers drive `usePreflight`/`useAnalyze`/`useUpdate
 * Mapping`/`useFinalize` via `mutateAsync()` and proceed off the resolved
 * promise — NOT off reactive `mutation.data` (observer-scoped; lost if the firing
 * component unmounts mid-flight). These hooks expose the raw mutation; the
 * pipeline uses `mutateAsync`.
 *
 * Invalidation: finalize and mapping edits change what the history list/detail
 * show, so they invalidate the relevant keys. Preflight/analyze are stateless
 * derivations (no cached server resource), so they invalidate nothing.
 */

import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  infiniteQueryOptions,
  type UseQueryResult,
  type UseMutationResult,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';
import { apiClient } from '@repo/shared/client';
import { contractQueryOptions, MODELING_STATUS_KEY } from '~/lib/query';
import { createContractFetcher } from '~/lib/loader-fetch';
import {
  preflightContract,
  analyzeContract,
  createUploadContract,
  uploadListContract,
  uploadDetailContract,
  ingestStatusContract,
  deleteUploadContract,
  deleteUploadFileContract,
  updateMappingContract,
  finalizeContract,
  batchReadinessContract,
  resolveUploadPlanContract,
  createUploadPlanContract,
  updateUploadPlanContract,
} from '@repo/shared/common';
import type {
  FileType,
  PreflightRequest,
  PreflightResponse,
  AnalyzeResponse,
  CreateUploadResponse,
  UploadListResponse,
  UploadDetailResponse,
  IngestStatusResponse,
  DeleteUploadResponse,
  DeleteUploadFileResponse,
  UpdateMappingRequest,
  UpdateMappingResponse,
  FinalizeRequest,
  FinalizeResponse,
  BatchReadinessRequest,
  BatchReadinessResponse,
  ResolveUploadPlanResponse,
  CreateUploadPlanRequest,
  CreateUploadPlanResponse,
  UpdateUploadPlanRequest,
  UpdateUploadPlanResponse,
} from '@repo/shared/common';

/** Root key — every upload query nests under it for blanket invalidation. */
export const UPLOADS_KEY = ['uploads'] as const;

/** Hierarchical query keys (frontend-state skill: keys are the cache's API). */
export const uploadKeys = {
  all: UPLOADS_KEY,
  list: (fileType: FileType | 'all') => [...UPLOADS_KEY, 'list', fileType] as const,
  detail: (uploadId: string) => [...UPLOADS_KEY, 'detail', uploadId] as const,
  /** Event-scoped ingest progress (A1) — polled by the B1 progress rail. */
  ingestStatus: (eventId: string) => [...UPLOADS_KEY, 'ingest-status', eventId] as const,
  /** Session entry resolve (latest plan values + isFirstPlan). */
  plan: [...UPLOADS_KEY, 'plan'] as const,
} as const;

// ── Preflight (mutation: headers → suggestions) ──────────────────────────────

/**
 * Run preflight on a file's headers. Stateless derivation — invalidates nothing.
 * Drive with `mutateAsync({ body })` from the flow.
 */
export function usePreflight(): UseMutationResult<
  PreflightResponse,
  Error,
  PreflightRequest
> {
  return useMutation<PreflightResponse, Error, PreflightRequest>({
    mutationFn: (body) => apiClient(preflightContract, { body }),
  });
}

// ── Analyze (mutation: scrubbed upload → analysis) ───────────────────────────

/**
 * Analyze a scrubbed upload (inference + mapping suggestions + previous-match +
 * modeling-readiness). The R2 object must already be PUT (the scrub pipeline does
 * that). Stateless derivation against the uploaded object — invalidates nothing.
 * Drive with `mutateAsync({ uploadFileId })` (analyze is per-FILE — A6).
 */
export function useAnalyze(): UseMutationResult<
  AnalyzeResponse,
  Error,
  { readonly uploadFileId: string }
> {
  return useMutation<AnalyzeResponse, Error, { readonly uploadFileId: string }>({
    mutationFn: ({ uploadFileId }) => apiClient(analyzeContract, { params: { uploadFileId } }),
  });
}

// ── Ingest status (A1 — event-scoped progress; the B1 progress rail's source) ──

/**
 * The B1 progress rail's honest source of truth: poll the event-scoped ingest
 * status (A1) — per-file `rowsIngested / rowsTotal` + `ingestCompleted`, plus the
 * event `allIngested` terminal signal — read off the durable per-window cursor the
 * ingest transactions commit (never blocking on ingest work, monotonic across
 * polls). Replaces the old blind 409-poll-on-analyze with its 10-minute budget.
 *
 * Polls every `POLL_MS` while `enabled` and the event is not yet fully ingested;
 * stops once `allIngested` (the rail then waits on analyze readiness, learned from
 * the detail read). `enabled` is false before an event id exists (pre-scrub) and on
 * a resumed draft (already ingested). Reads carry no PII (post-scrub, id-only).
 */
export const INGEST_STATUS_POLL_MS = 2_500;

export function useIngestStatus(
  eventId: string | null,
  enabled: boolean,
): UseQueryResult<IngestStatusResponse> {
  return useQuery({
    queryKey: eventId !== null ? [...uploadKeys.ingestStatus(eventId)] : [...uploadKeys.ingestStatus('')],
    queryFn: () => apiClient(ingestStatusContract, { params: { eventId: eventId ?? '' } }),
    enabled: enabled && eventId !== null,
    // Keep polling until every file's ingest is complete; then stop (analyze
    // readiness is learned separately). `refetchInterval` reads the latest data.
    refetchInterval: (query) =>
      query.state.data?.event.allIngested === true ? false : INGEST_STATUS_POLL_MS,
    refetchIntervalInBackground: true,
    // Progress is inherently live — never serve a stale cached value across mounts.
    staleTime: 0,
    gcTime: 0,
  });
}

// ── Create upload event (mint the shared event id once per session) ──────────

/**
 * Mint a draft upload EVENT — the shared `Upload` every file in this session
 * attaches to. The wizard calls this ONCE before the first scrub and threads the
 * returned id into every file's transfer-init. Invalidates nothing: the bare
 * draft has no files yet, and finalize/mapping invalidate the list once files
 * exist. Drive with `mutateAsync()`.
 */
export function useCreateUpload(): UseMutationResult<CreateUploadResponse, Error, void> {
  return useMutation<CreateUploadResponse, Error, void>({
    mutationFn: () => apiClient(createUploadContract, { body: {} }),
  });
}

// ── Upload history (infinite/cursor query) ───────────────────────────────────

/**
 * Cursor-paginated upload-history options (ADHERENCE: lists use cursor
 * pagination), backed by the isomorphic fetcher so a route loader and the
 * component share one definition. The contract returns `nextCursor`;
 * `getNextPageParam` feeds it back as the `cursor` query param. `fileType`
 * filters server-side ('all' omits the filter).
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `infiniteQueryOptions` brand; the response generic keeps callers typed.
export function uploadsListInfiniteQueryOptions(fileType: FileType | 'all' = 'all') {
  const fetchPage = createContractFetcher(uploadListContract);
  return infiniteQueryOptions<
    UploadListResponse,
    Error,
    InfiniteData<UploadListResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: [...uploadKeys.list(fileType)],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      fetchPage({
        query: {
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
          ...(fileType !== 'all' ? { fileType } : {}),
        },
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

/** Cursor-paginated upload history. Prefer the loader to warm the cache first. */
export function useUploadsList(
  fileType: FileType | 'all' = 'all',
): UseInfiniteQueryResult<InfiniteData<UploadListResponse>, Error> {
  return useInfiniteQuery(uploadsListInfiniteQueryOptions(fileType));
}

// ── Upload detail (reopen) ───────────────────────────────────────────────────

/**
 * Options for one historical file's detail (analysis + persisted mapping +
 * status), backed by the isomorphic fetcher and shared with the route loader.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand; the contract keeps callers typed.
export function uploadDetailQueryOptions(eventId: string) {
  return contractQueryOptions(uploadDetailContract, [...uploadKeys.detail(eventId)], {
    params: { eventId },
  });
}

/** Load one historical file's detail. Prefer the loader to warm the cache first. */
export function useUploadDetail(
  uploadId: string,
): UseQueryResult<UploadDetailResponse> {
  return useQuery({
    ...uploadDetailQueryOptions(uploadId),
    // Guard the non-route callers that may pass an empty id before one is known.
    enabled: uploadId !== '',
  });
}

// ── Update mapping (PUT; invalidate list + this detail) ──────────────────────

/**
 * Persist a file's mapping in isolation (PRODUCT: affects only this file). On
 * success invalidates the upload list and this file's detail so both refetch the
 * persisted state. Drive with `mutateAsync({ uploadFileId, mapping })`.
 */
export function useUpdateMapping(): UseMutationResult<
  UpdateMappingResponse,
  Error,
  { readonly uploadFileId: string } & UpdateMappingRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    UpdateMappingResponse,
    Error,
    { readonly uploadFileId: string } & UpdateMappingRequest
  >({
    mutationFn: ({ uploadFileId, mapping }) =>
      apiClient(updateMappingContract, { params: { uploadFileId }, body: { mapping } }),
    async onSuccess(_data, { uploadFileId }) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...UPLOADS_KEY] }),
        queryClient.invalidateQueries({ queryKey: [...uploadKeys.detail(uploadFileId)] }),
      ]);
    },
  });
}

// ── Soft delete (event / file; invalidate portfolio-facing upload reads) ─────

export function useDeleteUpload(): UseMutationResult<
  DeleteUploadResponse,
  Error,
  { readonly eventId: string }
> {
  const queryClient = useQueryClient();
  return useMutation<DeleteUploadResponse, Error, { readonly eventId: string }>({
    mutationFn: ({ eventId }) =>
      apiClient(deleteUploadContract, { params: { eventId } }),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...UPLOADS_KEY] }),
        // A delete dirties the modeling dataset — re-arm the block-until-settled state on the
        // Modeling + Portfolio screens immediately, not on their next stale interval.
        queryClient.invalidateQueries({ queryKey: [...MODELING_STATUS_KEY] }),
      ]);
    },
  });
}

export function useDeleteUploadFile(): UseMutationResult<
  DeleteUploadFileResponse,
  Error,
  { readonly uploadFileId: string; readonly parentUploadId: string }
> {
  const queryClient = useQueryClient();
  return useMutation<
    DeleteUploadFileResponse,
    Error,
    { readonly uploadFileId: string; readonly parentUploadId: string }
  >({
    mutationFn: ({ uploadFileId }) =>
      apiClient(deleteUploadFileContract, { params: { uploadFileId } }),
    async onSuccess(_data, { parentUploadId }) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...UPLOADS_KEY] }),
        queryClient.invalidateQueries({ queryKey: [...uploadKeys.detail(parentUploadId)] }),
        // Removing a file dirties the modeling dataset — re-arm the block immediately.
        queryClient.invalidateQueries({ queryKey: [...MODELING_STATUS_KEY] }),
      ]);
    },
  });
}

// ── Batch readiness (read-only validation preview) ───────────────────────────

/**
 * Compute the batch's validation preview WITHOUT accepting anything. The response
 * contains the legacy aggregate modeling-readiness plus row-level loan-data
 * validation, which is the source of truth for whether finalization can proceed.
 * Stateless derivation — invalidates nothing. Drive with `mutateAsync({ uploadIds })`.
 */
export function useBatchReadiness(): UseMutationResult<
  BatchReadinessResponse,
  Error,
  BatchReadinessRequest
> {
  return useMutation<BatchReadinessResponse, Error, BatchReadinessRequest>({
    mutationFn: (body) => apiClient(batchReadinessContract, { body }),
  });
}

// ── Finalize (batch accept; invalidate the list) ─────────────────────────────

/**
 * Batch-accept the uploads after validation passes. On success invalidates the
 * whole uploads tree so the history list shows the newly-accepted files. Drive
 * with `mutateAsync({ uploadIds })`.
 */
export function useFinalize(): UseMutationResult<
  FinalizeResponse,
  Error,
  FinalizeRequest
> {
  const queryClient = useQueryClient();
  return useMutation<FinalizeResponse, Error, FinalizeRequest>({
    mutationFn: (body) => apiClient(finalizeContract, { body }),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...UPLOADS_KEY] }),
        // Accepting new loans dirties the modeling dataset — re-arm the block-until-settled state on
        // the Modeling + Portfolio screens right away so they show "still processing" immediately.
        queryClient.invalidateQueries({ queryKey: [...MODELING_STATUS_KEY] }),
      ]);
    },
  });
}

// (A6: `useAdvanceUploadProgress` + the `POST /:id/progress` contract were removed.
// The wizard no longer POSTs a furthest-step high-water mark; a draft's resume step
// is derived server-side from its artifacts — `resumeStep` on the detail read.)

// ── Upload plan (resolve on entry; update on guide completion) ────────────────

/**
 * Options for the session's entry resolve (latest plan VALUES + `isFirstPlan`),
 * backed by the isomorphic fetcher and shared with the `/uploads/new` route loader —
 * so a client navigation prefetches the entry decision (guide vs skip) and the page
 * arrives ready instead of doing a "Preparing your upload" round-trip. Created/updated
 * plans invalidate `uploadKeys.plan` so a later re-entry re-resolves.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function resolveUploadPlanQueryOptions() {
  return contractQueryOptions(resolveUploadPlanContract, [...uploadKeys.plan]);
}

/**
 * Resolve THIS upload session's entry — a READ (`GET /api/uploads/plan`): the server
 * returns the org's most-recent plan's VALUES (to seed the "Your plan" card) or a
 * default, plus `isFirstPlan` (→ show the guide). It does NOT mint a row, so opening
 * the page never dismisses the first-run guide. Still exposed as a mutation for the
 * imperative "Start Another" re-resolve (where a just-created plan must be re-read
 * fresh); the page-entry path reads {@link resolveUploadPlanQueryOptions} from the
 * loader-warmed cache instead.
 */
export function useResolveUploadPlan(): UseMutationResult<
  ResolveUploadPlanResponse,
  Error,
  void
> {
  return useMutation<ResolveUploadPlanResponse, Error, void>({
    mutationFn: () => apiClient(resolveUploadPlanContract),
  });
}

/**
 * Create THIS session's plan row (guide completion / Skip). The new row becomes the
 * org's latest → the next upload duplicates it forward. Drive with `mutateAsync(fields)`.
 * Invalidates `uploadKeys.plan` so a later page-entry resolve re-reads the new latest.
 */
export function useCreateUploadPlan(): UseMutationResult<
  CreateUploadPlanResponse,
  Error,
  CreateUploadPlanRequest
> {
  const queryClient = useQueryClient();
  return useMutation<CreateUploadPlanResponse, Error, CreateUploadPlanRequest>({
    mutationFn: (body) => apiClient(createUploadPlanContract, { body }),
    // This row becomes the org's latest → the cached entry resolve is now stale.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...uploadKeys.plan] });
    },
  });
}

/**
 * Update this session's EXISTING plan (a second Restart-Guide pass in the same
 * session, after the row was created). Drive with `mutateAsync({ planId, ...fields })`.
 * Invalidates `uploadKeys.plan` so a later page-entry resolve re-reads the edit.
 */
export function useUpdateUploadPlan(): UseMutationResult<
  UpdateUploadPlanResponse,
  Error,
  { readonly planId: string } & UpdateUploadPlanRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    UpdateUploadPlanResponse,
    Error,
    { readonly planId: string } & UpdateUploadPlanRequest
  >({
    mutationFn: ({ planId, ...fields }) =>
      apiClient(updateUploadPlanContract, { params: { planId }, body: fields }),
    // Edited plan values → the cached entry resolve is now stale.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...uploadKeys.plan] });
    },
  });
}
